import http from "./http-common";
import authHeader from "./auth-header";
import process from "shelljs";
import qs from "qs";

const API_URL = process.env.BFF_URL;

class InvoiceService {

  findDocs(params) {
    // Get customer context - this is a customer portal, so we always need customer ID
    const selectedContext = localStorage.getItem('selectedContext');
    
    if (!selectedContext) {
      console.error('No selectedContext found in localStorage');
      throw new Error('Customer context is required');
    }
    
    let customerId = null;
    try {
      const context = JSON.parse(selectedContext);
      // Use contextId as the customer ID
      customerId = context.contextId;
    } catch (e) {
      console.error('Failed to parse selectedContext:', e);
      throw new Error('Invalid customer context');
    }
    
    if (!customerId) {
      console.error('No customer ID (contextId) found in context');
      throw new Error('Customer ID is required');
    }
    
    // Always use customer-specific endpoint in customer portal
    return http.get(API_URL + `/v1/customers/${customerId}/invoices`, {
      params: params, paramsSerializer: params => {
        return qs.stringify(params, {arrayFormat: 'repeat'})
      }, headers: authHeader()
    });
  }

  downloadInvoicesAsExcel(params) {
    return http.get(API_URL + `/app/invoices/download/excel`, {
      params: params, paramsSerializer: params => {
        return qs.stringify(params, {arrayFormat: 'repeat'})
      }, headers: authHeader()
    }).then();
  }

  downloadInvoicesAsPdf(params) {
    return http.get(API_URL + `/app/invoices/download/pdf`, {
      params: params, paramsSerializer: params => {
        return qs.stringify(params, {arrayFormat: 'repeat'})
      }, headers: authHeader()
    }).then();
  }

  findAll(page, size) {
    return http.get(API_URL + "/invoices?page=" + page + "&size=" + size, {headers: authHeader()})
  }

  findAllDocs(page, size, type = 'INVOICE') {
    return http.get(API_URL + "/invoices?page=" + page + "&size=" + size + "&type=" + type, {headers: authHeader()})
  }

  findByOrderNumber(page, size, type, orderNumber) {
    return http.get(API_URL + "/invoices?page=" + page + "&size=" + size + "&type=" + type + "&orderNumber=" + orderNumber, {headers: authHeader()})
  }

  draft(invoice) {
    return http.post(API_URL + "/invoices/draft", invoice, {headers: authHeader()});
  }

  create(invoice) {
    return http.post(API_URL + "/app/invoices", invoice, {headers: authHeader()});
  }

  fiscalise(id, invoice) {
    return http.put(API_URL + `/app/invoices/${id}/report`, invoice, {headers: authHeader()});
  }

  update(id, invoice) {
    return http.put(API_URL + `/app/invoices/${id}`, invoice, {headers: authHeader()});
  }

  pay(id, data) {
    return http.post(API_URL + `/app/invoices/${id}/pay`, data, {headers: authHeader()});
  }

  cancel(id) {
    return http.put(API_URL + `/app/invoices/${id}/cancel`, null, {headers: authHeader()});
  }

  markAsNotTaxable(id) {
    return http.put(API_URL + `/invoices/${id}/not-taxable`, null, {headers: authHeader()});
  }

  markAsSent(id) {
    return http.put(API_URL + `/app/invoices/${id}/sent`, null, {headers: authHeader()});
  }

  approve(id) {
    return http.put(API_URL + `/app/invoices/${id}/approve`, null, {headers: authHeader()});
  }

  reconcile(id) {
    return http.put(API_URL + `/app/invoices/${id}/reconcile`, null, {headers: authHeader()});
  }

  clone(id) {
    return http.put(API_URL + `/invoices/${id}/clone`, null, {headers: authHeader()});
  }

  convertToOrder(id) {
    return http.put(API_URL + `/invoices/${id}/order`, null, {headers: authHeader()});
  }

  convertToInvoice(id) {
    return http.put(API_URL + `/invoices/${id}/invoice`, null, {headers: authHeader()});
  }

  decline(id) {
    return http.put(API_URL + `/invoices/${id}/decline`, null, {headers: authHeader()});
  }

  findById(id) {
    // Get customer context - this is a customer portal, so we always need customer ID
    const selectedContext = localStorage.getItem('selectedContext');
    
    if (!selectedContext) {
      console.error('No selectedContext found in localStorage');
      throw new Error('Customer context is required');
    }
    
    let customerId = null;
    try {
      const context = JSON.parse(selectedContext);
      // Use contextId as the customer ID
      customerId = context.contextId;
    } catch (e) {
      console.error('Failed to parse selectedContext:', e);
      throw new Error('Invalid customer context');
    }
    
    if (!customerId) {
      console.error('No customer ID (contextId) found in context');
      throw new Error('Customer ID is required');
    }
    
    // Always use customer-specific endpoint in customer portal
    return http.get(API_URL + `/v1/customers/${customerId}/invoices/${id}`, {headers: authHeader()});
  }

  getInvoicePdf(id) {
    // Get customer context - this is a customer portal, so we always need customer ID
    const selectedContext = localStorage.getItem('selectedContext');
    
    if (!selectedContext) {
      console.error('No selectedContext found in localStorage');
      throw new Error('Customer context is required');
    }
    
    let customerId = null;
    try {
      const context = JSON.parse(selectedContext);
      // Use contextId as the customer ID
      customerId = context.contextId;
    } catch (e) {
      console.error('Failed to parse selectedContext:', e);
      throw new Error('Invalid customer context');
    }
    
    if (!customerId) {
      console.error('No customer ID (contextId) found in context');
      throw new Error('Customer ID is required');
    }
    
    // Always use customer-specific endpoint in customer portal
    return http.get(API_URL + `/v1/customers/${customerId}/invoices/${id}/pdf`, {headers: authHeader()});
  }


  deleteAll() {
    return http.delete(API_URL + `/app/invoices`, {headers: authHeader()})
  }

  delete(id) {
    return http.delete(API_URL + `/app/invoices/${id}`, {headers: authHeader()})
  }

  syncInvoice(id) {
    return http.get(API_URL + `/app/invoices/${id}/sync`, {headers: authHeader()})
  }

  sendViaEmail(id, data) {
    return http.post(API_URL + `/app/invoices/${id}/email`, data, {headers: authHeader()});
  }

  addInvoiceLine(id, invoiceLine) {
    return http.post(API_URL + `/invoices/${id}/items`, invoiceLine, {headers: authHeader()});
  }

  removeInvoiceLine(id, sku) {
    return http.delete(API_URL + `/invoices/${id}/items/${sku}`, {headers: authHeader()});
  }

  addPayment(id, payment) {
    return http.post(API_URL + `/app/invoices/${id}/payments`, payment, {headers: authHeader()});
  }

  getReport() {
    return http.get(API_URL + "/api/reports", {headers: authHeader()})
  }

  billing(mid, billing) {
    return http.post(API_URL + `/merchants/${mid}/billing`, billing, {headers: authHeader()});
  }

  syncInvoices(data) {
    return http.post(API_URL + "/app/invoices/pull", data, {headers: authHeader()});
  }

  pushInvoices(data) {
    return http.post(API_URL + "/app/invoices/push", data, {headers: authHeader()});
  }

  handleResponse(response) {
    return response
  }
}

export default new InvoiceService();



// WEBPACK FOOTER //
// ./src/service/invoice.service.js